
import { createCanvas } from "../quadra";
import { IGame } from "../types/game";
import FlashLineExplosionEffect from "./effects/flash-line-explosion-effect";
import BlockQueueView from "./block-queue-view";
import BonusView from "./bonus-view";
import CanvasView from "./canvas-view";

export default class GameView {
    
    private game: IGame
    private screen: HTMLCanvasElement
    private context: CanvasRenderingContext2D
    private width: number
    public height: number
    private scale: number
    private padding: number
    private canvasView: CanvasView
    private bonusView: BonusView
    private blockQueueView: BlockQueueView
    private flashingLine: boolean
    private flashLineEffect: FlashLineExplosionEffect

    constructor(screen: HTMLCanvasElement, game: IGame, width: number = 400, scale: number = 1){
        this.screen = screen;
        this.context = screen.getContext('2d');
        if (this.context) {
            // Disable image smoothing for crisp pixel rendering
            this.context.imageSmoothingEnabled = false;
            this.context.imageSmoothingQuality = "low";
        }
        this.game = game
        this.screen.width = this.width = width;
        this.padding = this.width * 0.05
        this.scale = scale;
        this.init();
    }

    private init(): void {
        // Calculate dimensions for a layout where:
        // - Queue is at top
        // - Board is centered below queue
        // - Sidebar (BonusView) is to the right of the board (garbage indicator)
        // - Border wraps Canvas + BonusView
        
        // Total Width Analysis:
        // We have width.
        // Sidebar takes ~1 block width + margins?
        // Board takes 10 blocks width.
        // Let's assume standard proportions.
        
        // Let's allocate 60% width to the board area (like before), but split it.
        // Actually, let's base everything on the container width.
        
        // Available width for the "Game Area" (Bonus + Board)
        const gameAreaWidth = this.width; 
        
        // Proportions: Bonus is roughly 10% of game area, Board is 90%?
        // Board is usually 10 blocks wide. Bonus is 1 block wide.
        // So 11 units total width.
        const unitSize = gameAreaWidth / 11;
        
        const boardWidth = unitSize * 10;
        const bonusWidth = unitSize * 1;
        
        // Initialize views
        this.canvasView = new CanvasView(createCanvas(), this.game, boardWidth);
        this.bonusView = new BonusView(createCanvas(), this.game, bonusWidth);
        
        const blockSize = this.canvasView.blockSize; // Should match unitSize roughly
        
        // Queue View takes full width at top
        this.blockQueueView = new BlockQueueView(createCanvas(), this.game, boardWidth, blockSize);

        const canvasViewHeight = this.canvasView.height;
        const queueHeight = this.blockQueueView.height;
        
        // Total height calculation
        // Top padding + Queue + Middle Padding + Board Height + Bottom Padding
        this.height = this.padding + queueHeight + (this.padding / 2) + canvasViewHeight + this.padding;
        this.screen.height = this.height;
        
        this.flashLineEffect = new FlashLineExplosionEffect(
            createCanvas(this.width, this.height), 
            this.padding, 
            this.padding + queueHeight + (this.padding / 2), // Offset to board start Y
            boardWidth, 
            this.game.palette
        );
    }

    resize(width: number): void {
        this.width = width;
        this.screen.width = this.width;
        this.padding = this.width * 0.05;
        this.init();
    }

    draw(frameTime: number){
        this.context.clearRect(0, 0, this.screen.width, this.screen.height)
        
        // Center the "Game Area" (Board + Bonus)
        const totalGameAreaWidth = this.canvasView.width + this.bonusView.width;
        const startX = (this.width - totalGameAreaWidth) / 2;
        
        // Calculate layout positions for Board and Bonus
        const queueHeight = this.blockQueueView.height;
        const gameAreaY = this.padding + queueHeight;

        // Draw Board on Left
        const boardX = startX;
        this.context.drawImage(this.canvasView.draw(frameTime), boardX, gameAreaY);
        
        // Draw Bonus (Garbage Sidebar) to the right of Board
        const bonusX = startX + this.canvasView.width;
        this.context.drawImage(this.bonusView.draw(), bonusX, gameAreaY);

        // Draw Flash Effect (overlays board)
        this.drawFlashAnim(boardX, gameAreaY);
        
        // Draw Border wrapping both Bonus and Board
        // We can use strokeRect for a simple border, or more complex if needed.
        // The original request said "border should cover the canvas and the garbage sidebar".
        this.drawBorder(startX, gameAreaY, totalGameAreaWidth, this.canvasView.height);
        
        // Draw Queue at the top
        this.context.drawImage(this.blockQueueView.draw(), 0, this.padding);
    }
    
    private drawBorder(x: number, y: number, width: number, height: number) {
        this.context.strokeStyle = "rgba(255, 255, 255, 0.5)";
        this.context.lineWidth = 4;
        // Draw outer border around Bonus + Board
        this.context.strokeRect(x, y, width, height);
        
        // Optional: Draw separator line between Bonus and Board?
        // this.context.beginPath();
        // this.context.moveTo(x + this.bonusView.width, y);
        // this.context.lineTo(x + this.bonusView.width, y + height);
        // this.context.stroke();
    }
    
    private drawFlashAnim(offsetX: number, offsetY: number) {
        const { flashBlocks, flashAnim } = this.game.canvas;
        if (flashAnim && !this.flashingLine) {
            this.flashingLine = true;
            this.flashLineEffect.enter(flashBlocks);
        }
        if (flashAnim) {
            // Offset flash effect to match board position
            this.context.drawImage(this.flashLineEffect.draw(flashAnim), offsetX - this.padding, 0); 
            // Note: FlashLineExplosionEffect might need refactoring if it relies on hardcoded internal offsets
            // But for now we just draw it. 
            // Actually, FlashLineExplosionEffect constructor took global coordinates/offsets.
            // We need to make sure we're not double-offsetting.
            
            // Current FlashLineExplosionEffect logic uses internal padding. 
            // Let's just rely on the fact that it was initialized with correct offsets in init().
            // Wait, init() passed `this.padding` as x offset. 
            // But now boardX is dynamic (`startX + bonusWidth`).
            
            // FIX: We should probably update FlashLineExplosionEffect to accept an offset at draw time or re-init it.
            // Ideally, we just construct it to match the board's rect.
            // Since resize() calls init(), we can update the effect there.
            // BUT, `startX` is calculated in `draw()`. We should calculate it in `init()`/`resize()` to be consistent.
        } else {
            this.flashingLine = false;
        }
    }
}
